import { delay, takeUntil } from 'rxjs/operators';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  Renderer2,
  SimpleChanges
} from '@angular/core';
import { ReplaySubject } from 'rxjs';
import { MapHelper } from './map-helper';
import { DomSanitizer } from '@angular/platform-browser';
import { BaseMapConfig, BaseMapModel, ICluster } from './interfaces/base-map.interface';
import { LeafletService, WindyService } from '@wind-log_library/fe-component-library';


@Component({
    selector: 'app-base-map',
    templateUrl: './base-map.component.html',
    styleUrls: ['./base-map.component.scss'],
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class BaseMapComponent implements OnInit, OnChanges, OnDestroy {
  @Input() data: any;
  @Input() initialCenter: number[];
  @Input() config: BaseMapConfig;

  initialZoom: number = 5;
  headerPositionsAdjust = 0;
  unit: string = 'MW';

  public selectedParksTurbines;
  public markers: any[] = [];
  public show = false;
  public selectedStatusTurbines: Array<any> = [];

  protected currentZoom = 8;
  protected unsubscriber = new ReplaySubject();
  protected clusters: ICluster[] = [];
  protected map;
  protected markersOnMap = [];
  protected parksData: any;
  protected parkTooltipId;
  protected maxZoom = 20;
  protected minZoom = 3;
  protected leafletMarkers: any[] = [];
  protected markerTooltipId;
  protected markerTooltipStatus: number;
  protected preventBubbledStatusesChange;
  protected frontMarkerDomId;

  private geomapMarkerIconWind = {
    iconUrl: 'assets/images/techs/wind-park-light.svg',
    iconSize: [50, 50],
    iconAnchor: [30, 30],
  };

  public static rectIntersect(rect1, rect2) {
    return rect1[0] < rect2[2] && rect2[0] < rect1[2] && rect1[1] < rect2[3] && rect2[1] < rect1[3];
  }

  constructor(protected leaflet: LeafletService,
              protected ngZone: NgZone,
              protected windy: WindyService,
              protected cdr: ChangeDetectorRef,
              protected renderer: Renderer2,
              public sanitizer: DomSanitizer) {
  }

  ngOnInit(): void {
    this.currentZoom = this.initialZoom;
    this.initWindyMap();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.data) {
      this.handleNewData(changes.data.isFirstChange);
    }
    if (!this.unit) {
      this.unit = 'MW';
    }
  }

  ngOnDestroy() {
    this.cdr.detach();
    this.unsubscriber.next(true);
    this.unsubscriber.complete();
    this.destroyMarkers();
    this.clusters = [];
    this.markersOnMap = [];
    this.windy.destroyScripts();
  }

  public trackByParkName = (park) => {
    return park.code;
  }

  public monitorPortfolioMarkerClicked = (markerDomId) => {
    this.bringCardToFront(markerDomId);
    this.redrawMap(0);
  }

  public markerIconClicked(marker): void {
    if (marker.id.startsWith('cluster-')) {
      this.zoomToCluster(marker);
    } else {
      this.parkClicked(marker.data.parks[0]);
    }
  }

  public trackByMarkerId = (marker) => {
    return marker.id;
  }

  public parkClicked(park): void {
    if (this.config.parkClicked) {
      this.config.parkClicked(park);
    }
  }

  public turbineClicked(turbine): void {
    if (this.config.turbineClicked) {
      this.config.turbineClicked(turbine);
    }
  }

  public redrawMapClick(): void {
    this.redrawMap(0);
  }

  public chartClicked(e: any): void {
    this.ngZone.runOutsideAngular(() => {
      const chartClass = e.active[0];

      if (chartClass) {
        const markerId = chartClass._chart.canvas.attributes.getNamedItem('data-marker-id').value;
        const chartStatus = chartClass._model.label;

        this.toggleMarkerTooltip(chartStatus, markerId);
      }
    });

  }

  public parkIconClicked = (name, markerDomId) => {
    this.bringCardToFront(markerDomId);
    this.toggleParkTooltip(name);
    this.redrawMap(0);
  }

  public isActiveParkTooltip = (name) => {
    return !!name && name !== '' && this.parkTooltipId === name;
  }

  public isActiveMarkerTooltip = (markerId) => {
    return markerId !== '' && this.markerTooltipId === markerId;
  }

  public preventClickOnPark(marker): string {
    return !!this.config && !!this.config.parkClicked || marker.id.startsWith('cluster-') ? '' : 'prevent-park-click';
  }

  protected getActiveStates() {
    return this.config.getActiveStates();
  }

  protected getStateColors(states: Array<number | string>): string[] {
    return this.config.getStateColors(states);
  }

  protected getStatusCountsForMarker(parksData, states, marker): number {
    return this.config.getStatusCountsForMarker(parksData, states, marker);
  }

  protected getMarkerData(marker, parksData?: BaseMapModel): any {
    return this.config.getMarkerData(marker, parksData);
  }

  protected calculateClustersWithDetails(minZoom, maxZoom, parksData, mapComponent?): ICluster[] {
    return this.config.calculateClustersWithDetails(minZoom, maxZoom, parksData, mapComponent);
  }

  protected redrawMap(timout: number = 100): void {
    const refresh = () => {
      this.selectedParksTurbines = this.setParkTurbines(this.parksData, this.parkTooltipId);
      this.drawMissingMarkersForZoomLevel();
      this.displayMarkersForZoomLevel();
      this.show = true;
      /* tslint:disable:no-string-literal */
      if (!this.cdr['destroyed']) {
        this.cdr.detectChanges();
      }
    };

    this.ngZone.run(() => {
      this.ngZone.runOutsideAngular(() => {
        this.show = !this.config.parkClicked;
        this.buildMarkersFromClusters();
        timout === 0 ? refresh() : setTimeout(refresh, !this.show ? timout : 0);
      });
    });
  }

  protected calculateClusters(): void {
    if (this.parksData) {
      this.clusters = this.calculateClustersWithDetails(this.minZoom, this.maxZoom, this.parksData, BaseMapComponent);
    }
  }

  protected buildMarkersFromClusters(): void {
    const markerData = [].concat(...this.clusters.filter(c => c.zoom === this.currentZoom).map(cluster =>
      cluster.markers.map(marker => {
        return {
          id: marker.id,
          zoom: cluster.zoom,
          latLon: marker.latLon,
          data: this.getMarkerData(marker, this.parksData),
          statusCounts: this.getStatusCountsForMarker(this.parksData, this.getActiveStates(), marker)
        };
      })
    ));

    this.markers.filter(m => m.zoom === this.currentZoom).forEach(m => {
      const newData = markerData.find(m1 => m1.id === m.id);
      if (newData) {
        m.data = newData.data;
        m.chart.turbineStatusesSums = newData.statusCounts;
      }
    });

    const currentIds = this.markers.map(m => m.id);
    this.markers = this.markers.concat(...markerData.filter(m => !currentIds.includes(m.id)).map(md =>
      Object.assign(md, {
        icon: this.geomapMarkerIconWind,
        chart: MapHelper.getChart(this.getActiveStates(), this.getStateColors(this.config.getActiveStates()), md.id, md.statusCounts)
      })));
  }

  protected drawMissingMarkersForZoomLevel(): void {
    this.markers.filter(m => m.zoom === this.currentZoom && !this.isMarkerOnMap(this.markersOnMap, m))
      .forEach(marker => {
        this.leafletSetMarkerGroupWithRetries(marker);
      });
  }

  protected displayMarkersForZoomLevel = () => {
    this.hideAllMarkers();
    this.showMarkersForZoom(this.currentZoom);
  }

  protected initWindyMap(): void {
    this.calculateClusters();
    let middleLatLong = [];
    const clustersForZoom = this.clusters.find(c => c.zoom === this.currentZoom);
    if (clustersForZoom) {
      const minLat = Math.min(...clustersForZoom.markers.map(m => m.latLon[0]));
      const minLon = Math.min(...clustersForZoom.markers.map(m => m.latLon[1]));
      const maxLat = Math.max(...clustersForZoom.markers.map(m => m.latLon[0]));
      const maxLon = Math.max(...clustersForZoom.markers.map(m => m.latLon[1]));

      middleLatLong = [(minLat + maxLat) / 2, (minLon + maxLon) / 2];
    }
    this.windy.init(
      middleLatLong[0],
      middleLatLong[1],
      'wind',
      this.config.getWindyKey()
    );
    this.redrawMap();

    this.windy.myCallback
      .pipe(
        takeUntil(this.unsubscriber)
      ).subscribe((val) => {
      switch (val.event) {
        case 'redrawMap':
          this.map = val.map;
          break;
        case 'zoomend':
          this.currentZoom = val.map.getZoom();
          this.toggleMarkerTooltip(this.markerTooltipStatus, this.markerTooltipId);
          this.calculateClusters();
          this.redrawMap();
          break;
        case 'redrawFinished':
          this.calculateClusters();
          this.redrawMap();
          setTimeout(() => {
            this.show = true;
            this.cdr.detectChanges();
          });
          break;
        case 'rqstOpen':
          setTimeout(() => {
            this.windy.removeHTML('plugin-menu');
          }, 100);
      }
      setTimeout(() => {
        this.windy.invalidateMap();
        this.cdr.detectChanges();
      }, 100);
    });
  }

  protected setParkTurbines(parksData, name) {
    if (parksData) {
      const park = parksData.assets.find(pd => pd.name === name);
      return park ? park.subAssets : [];
    }
  }

  protected destroyMarkers = () => {
    this.markers = [];
    this.markersOnMap = [];
    this.ngZone.runOutsideAngular(() => {
      this.leafletMarkers.forEach(leafletMarker => {
        this.map.removeLayer(leafletMarker);
      });
      this.leafletMarkers = [];
    });
  }

  protected toggleMarkerTooltip(status, markerId) {
    this.closeParkTooltip();

    if (this.markerTooltipId === markerId && this.markerTooltipStatus === status) {
      this.markerTooltipId = undefined;
      this.markerTooltipStatus = undefined;
      return;
    }

    this.setClusterTurbines(status, markerId);

    this.markerTooltipStatus = status;
    this.markerTooltipId = markerId;
  }

  protected bringCardToFront = (markerDomId) => {
    const currentHighestMarker = document.getElementById(this.frontMarkerDomId);
    if (currentHighestMarker) {
      const parent = currentHighestMarker.parentElement;
      this.renderer.setStyle(parent, 'z-index', 100);
      const currentHighestMarkerImg = document.querySelector(`img.${this.frontMarkerDomId}`);
      this.renderer.setStyle(currentHighestMarkerImg, 'z-index', 101);
    }

    this.frontMarkerDomId = markerDomId;

    const markerElement = document.getElementById(markerDomId);
    if (markerElement) {
      const parent = markerElement.parentElement;
      this.renderer.setStyle(parent, 'z-index', 9999);
      const markerElementImg = document.querySelector(`img.${markerDomId}`);
      this.renderer.setStyle(markerElementImg, 'z-index', 10000);
    }
  }

  protected toggleParkTooltip = (name) => {
    this.closeMarkerTooltip();

    if (this.parkTooltipId === name) {
      this.parkTooltipId = undefined;
      return;
    }

    this.selectedParksTurbines = this.setParkTurbines(this.parksData, this.parkTooltipId);
    this.parkTooltipId = name;
    setTimeout(() => this.resizeStatusesCard(), 300);
  }

  private async leafletSetMarkerGroupWithRetries(marker: any, retries: number = 5): Promise<void> {
    try {
      this.leaflet.setMarkerGroup(this.map, marker, this.onMarkerDrawn).forEach(newMarker => {
        this.leafletMarkers.push(newMarker);
      });
    } catch (e) {
      if (retries > 0) {
        await delay(250);
        this.leafletSetMarkerGroupWithRetries(marker, retries - 1);
      }
    }
  }

  private onMarkerDrawn = marker => {
    this.ngZone.runOutsideAngular(() => {
      this.leaflet.showMarker(marker);
      this.markersOnMap.push(marker);
    });
  }

  private hideAllMarkers = () => {
    this.markers.forEach(marker => {
      const markerContainer = document.getElementById(marker.id);
      if (markerContainer) {
        markerContainer.style.pointerEvents = 'none';
        markerContainer.style.opacity = '0';
      }
      Array.from<any>(document.getElementsByClassName(marker.id))
        .forEach((markerIcon) => {
          markerIcon.style.pointerEvents = 'none';
          markerIcon.style.opacity = '0';
        });
    });
  }

  private showMarkersForZoom = zoom => {
    this.markers.filter(m => m.zoom === zoom).forEach(marker => {
      const markerContainer = document.getElementById(marker.id);
      if (markerContainer) {
        markerContainer.style.pointerEvents = 'all';
        markerContainer.style.opacity = '1';
      }
      const markerIcon = document.getElementsByClassName(marker.id)[0] as HTMLElement;
      if (markerIcon) {
        markerIcon.style.pointerEvents = 'all';
        markerIcon.style.opacity = '1';
      }
    });
  }

  private zoomToCluster(marker): void {
    const arraysEqual = (a1, a2) => a1.length === a2.length && a1.every((v, i) => v === a2[i]);
    let newZoom = this.currentZoom + 1;
    const markers = [].concat(...this.clusters.map(c => c.markers)).find(m => m.id === marker.id);
    if (markers) {
      newZoom = Math.max(...this.clusters.filter(c => c.markers.find(m => arraysEqual(m.parkNames, markers.parkNames)))
        .map(f => f.zoom)) + 1;
    }
    if (newZoom > this.maxZoom) {
      newZoom = this.maxZoom;
    }
    this.map.setView(marker.latLon, newZoom);
  }

  private handleNewData(firstData): void {

    if (this.parksData !== this.data) {

      // avoid drawing all clusters again and again when only the actual + theo values have changed:
      const onlyValuesChanged = this.parksData && this.data.assets && this.parksData.assets &&
        (this.parksData.assets.length === this.data.assets.length &&
          (this.parksData.assets[0].name === this.data.assets[0].name));

      this.parksData = this.data;

      if (onlyValuesChanged) {
        setTimeout(() => {
          this.calculateClusters();
          this.buildMarkersFromClusters();
        }, 500);
      } else {
        setTimeout(() => {
          if (this.data.assets.length > 0) {
            if (this.map) {
              this.map.panTo(this.initialCenter);
            } else {
              /* Add a timeout to wait for the map to be created*/
              setTimeout(() => {
                if (this.map) {
                  this.map.panTo(this.initialCenter);
                }
              }, 1500);
            }
          }

          this.destroyMarkers();
          // give some time to cleanup old markers when switching between assets
          setTimeout(() => {
            if (firstData) {
              const zoom = this.data.assets.length === 1 ? 6 : this.initialZoom;
              this.map?.setZoom(zoom);
            }
            this.calculateClusters();
            this.selectedParksTurbines = this.setParkTurbines(this.parksData, this.parkTooltipId);
            this.show = false;
            this.redrawMap();
          }, 500);
        }, 400);
      }
    }
  }

  private isMarkerOnMap(markersOnMap, marker) {
    return markersOnMap.find(m => m.id === marker.id) !== undefined;
  }

  closeParkTooltip = () => {
    this.parkTooltipId = undefined;
  }

  private setClusterTurbines = (status, markerId) => {
    if (this.preventBubbledStatusesChange) {
      return;
    }

    this.preventBubbledStatusesChange = true;
    setTimeout(() => this.preventBubbledStatusesChange = false, 300);
    this.selectedStatusTurbines = [];
    const marker = this.markersOnMap.find(m => m.id === markerId);
    if (marker) {
      const parkNames = marker.data.parks.map(p => p.name);
      this.selectedStatusTurbines = this.parksData.assets
      .filter(pd => parkNames.includes(pd.name))
      .reduce((turbines, pd) =>
      turbines.concat(pd.subAssets.filter((t: any) => t.status === status)), []);
    }
  }

  private closeMarkerTooltip = () => {
    this.markerTooltipId = undefined;
    this.markerTooltipStatus = undefined;
  }

  private resizeStatusesCard() {
    const geoMapRef = document.getElementsByTagName('app-geolocations-map')[0];
    if (!geoMapRef) {
      return;
    }
    const mapHeight = geoMapRef.clientHeight;
    const statusesCardRef = document.getElementsByClassName('statuses card')[0] as HTMLElement;

    if (statusesCardRef) {
      statusesCardRef.style.overflowY = 'auto';
      const mapTop = geoMapRef.getBoundingClientRect().top;
      const statusesTop = statusesCardRef.getBoundingClientRect().top;
      statusesCardRef.style.maxHeight = mapHeight - (statusesTop - mapTop) - 10 + 'px';
    }
  }
}